import { z } from 'zod'

import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import {
  basketBacktestQueryOptions,
  basketDetailQueryOptions,
  basketHistoryQueryOptions,
  basketInOutQueryOptions,
} from '@/features/baskets/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import { codeParamSchema } from '@/schemas/common'
import { pageSizeSchema } from '@/schemas/table'

/** The basket page's tabs, in drawn order (mock `?tab=members|past|how|io`, + history). */
export const BASKET_TABS = ['members', 'past', 'how', 'history', 'io'] as const
export type BasketTab = (typeof BASKET_TABS)[number]

/**
 * - `tab`: which tab is open; Members by default. The mock's old `check` is In or Out.
 * - `s`: the company whose verdict the In or Out tab shows, uppercased so a hand-typed link is
 *   the same cache entry and lights the same row.
 * - `page`, `pageSize`: the Members (or History) table's page, over the rows already in hand. 50 a page,
 *   so a hand-picked basket fits on one and a trader basket of up to ~150 takes three.
 *
 * Every field falls back instead of throwing: a stale or hand-edited value lands on the default
 * view, never an error page.
 */
export const basketTabSchema = z.preprocess(
  (value) => (value === 'check' ? 'io' : value),
  z.enum(BASKET_TABS),
)

export const basketDetailSearchSchema = z.object({
  tab: basketTabSchema.default('members').catch('members'),
  s: codeParamSchema
    .transform((symbol) => symbol.trim().toUpperCase())
    .pipe(z.string().min(1))
    .optional()
    .catch(undefined),
  page: z.number().int().min(1).default(1).catch(1),
  pageSize: pageSizeSchema.default(50).catch(50),
})
export type BasketDetailSearch = z.infer<typeof basketDetailSearchSchema>

export const BASKET_DETAIL_SEARCH_DEFAULTS = { tab: 'members', page: 1, pageSize: 50 } as const

/**
 * The `/baskets/$basketId` loader. `$basketId` is the basket's slug.
 *
 * MAIN: the basket, which the header, the strip and the Members and How It's Built tabs all
 * read. `null` (`BASKET_NOT_FOUND`) is the screen's not-found state, never this route's error.
 *
 * SECONDARY, only for a real navigation to `?tab=io` or `?tab=history`: that tab's list (In or Out
 * up to ~300 companies, History up to 200 stays), so a shared link lands with its tab already
 * loading. Neither is on the Members tab's path.
 */
export async function loadBasketDetail({
  context: { queryClient },
  params,
  location,
  preload,
}: {
  context: RouterContext
  params: { basketId: string }
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  const search = basketDetailSearchSchema.parse(location.search)
  if (!preload && search.tab === 'io') {
    prefetchRouteData(queryClient, basketInOutQueryOptions(params.basketId))
  }
  if (!preload && search.tab === 'history') {
    prefetchRouteData(queryClient, basketHistoryQueryOptions(params.basketId))
  }
  // Past results decides whether its tab is drawn at all, so it is asked on every real visit.
  if (!preload) prefetchRouteData(queryClient, basketBacktestQueryOptions(params.basketId))
  await ensureRouteData(queryClient, basketDetailQueryOptions(params.basketId))
}
